import { DefaultUser } from 'next-auth';
import { FieldState } from 'formstate';
import {
  AvailabilityType,
  CartProductPricesType,
  CartProductSizeType,
  CartTaxGroup,
  CartTaxesTotalGroup,
  CartTotalsType,
  CartTotalValueType,
  DeliveryType,
  ICartItem,
  ICategoriesProduct,
  ProductType,
} from '@features/cart/typescript';

interface IKeycloakJwt {
  accessToken: string;
  exp: number;
  iat?: number;
  idToken: string;
  jti?: string;
  refreshToken: string;
  refreshTokenExpires: string;
  sub?: string;
}

export interface IUserData {
  token?: IKeycloakJwt;
  user?: DefaultUser;
  expires?: string;
}
export interface ITaxRegimeData {
  code: string;
  description?: string;
}
export interface IAdditionalData {
  taxRegime?: string;
}

export interface IAddressV2PhoneNumber {
  countryCode: string;
  formatted?: string;
  number: string;
}

export type TAddressStateType = {
  country: FieldState<string | null>;
  firstName: FieldState<string | null>;
  lastName: FieldState<string | null>;
  street: FieldState<string | null>;
  streetNumber: FieldState<string | null>;
  apartmentNumber: FieldState<string | null>;
  postalCode: FieldState<string | null>;
  city: FieldState<string | null>;
  district: FieldState<string | null>;
  phoneNumber: FieldState<string | null>;
  phoneNumberCountryCode: FieldState<string | null>;
};

export enum TAddressType {
  delivery = '/api/address_types/1',
  invoice = '/api/address_types/2',
  base = '/api/address_types/3',
}

export enum TAddressTypeV2 {
  delivery = 'DELIVERY',
  invoice = 'INVOICE',
  base = 'BASIC',
}

export type TUserAddressType = {
  id?: number;
  active: boolean;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string;
  country?: string;
  province?: string | null;
  postalCode: string | null;
  district?: string;
  city?: string;
  street: string | null;
  streetNumber: string | null;
  apartmentNumber: string | null;
  phoneNumber?: string | null;
  phoneNumber2?: string | null;
  taxId?: string | null;
  email?: string | null;
  default?: boolean;
  type?: string;
  customerType?: string | null;
  additionalData?: IAdditionalData;
  taxRegime?: ITaxRegimeData;
};

export type TUserBillingType = {
  id?: number;
  active: boolean;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  country?: string;
  province?: string | null;
  postalCode: string;
  district?: string | null;
  city: string;
  street: string;
  streetNumber: string;
  apartmentNumber: string | null;
  phoneNumber?: string | IAddressV2PhoneNumber;
  phoneNumber2?: string | null;
  taxId?: string;
  email?: string | null;
  default?: boolean;
  type?: string;
  addressType?: string;
  customerType?: string | null;
  additionalData?: IAdditionalData;
  taxRegime?: string | ITaxRegimeData;
};

export enum TOrderOriginType {
  cart = 'cart',
  offer = 'offer',
}

export enum TOrderStatusType {
  draft = 'draft',
  pending = 'pending',
  registered = 'registered',
  unconfirmed = 'unconfirmed',
  confirmed = 'confirmed',
  for_approval = 'for_approval',
  canceled = 'canceled',
  in_progress = 'in_progress',
  realized = 'realized',
  editing = 'editing',
  rejected = 'rejected',
  shipped = 'shipped',
  partially_shipped = 'partially_shipped',
  completation = 'completation',
}

export enum TCartStatusType {
  draft = 'draft',
  canceled = 'canceled',
  ordered = 'ordered',
}

export enum TOrderPaymentStatusType {
  paid = 'paid',
  partially_paid = 'partially_paid',
  unpaid = 'unpaid',
}

export type TUserSimpleOrderType = {
  orderId: string;
  number: string;
  ownerId: string;
  customerId: string;
  status: string;
  createdDate: string;
  deliveries: [string];
  orderPaymentStatus: TOrderPaymentStatusType;
  grossTotalPrice: {
    amount: string;
    currency: string;
  };
  originType: TOrderOriginType;
  store: {
    street: string;
    city: string;
    district: string | null;
    postalCode: string;
  };
};

export type TUserCreativeType = {
  configurationId: string;
  name: string;
  description: string;
  imageUrl: string;
  customerId: string;
  createdDate: string;
  editedDate: string;
};

export type ProductAvailabilityType = {
  dateFrom: string;
  status: AvailabilityType;
  expiryDate: string;
  amount: number;
};

export type OrderTotalType = {
  totalType: string;
  total: {
    amount: number;
    currency: string;
  };
};

export type PositionType = {
  positionId: string;
  type: string;
  name: string;
  image: string;
  quantity: number;
  isAssemblable?: boolean;
  isBringable?: boolean;
  bringPrice?: {
    amount: number;
    currency: string;
  };
  assemblyPrice?: {
    amount: number;
    currency: string;
  };
  basePrice: number;
  productType: ProductType;
  size: CartProductSizeType;
  bringServicePositionId?: string;
  assemblyServicePositionId?: string;
  serviceId?: string;
  multiplication?: string;
  totalMultiplication?: string;
  quantityType?: string;
  unitType?: string;
  orderUnit?: string;
  priceUnit?: string;
  prices: CartProductPricesType;
  availability: ProductAvailabilityType;
  deliveryType?: DeliveryType;
  deliveryTypeId?: string;
  deliveryTypeNameLanguageKey?: string;
  nameLanguageKey?: string;
  isAssembleAvailable?: boolean;
  isBringingAvailable?: boolean;
  isPaidOnDelivery?: boolean;
  isTaxAvailable?: boolean;
  isVatAvailable?: boolean;
  leadTime?: number;
  logisticsTime?: number;
  personalPickupStoreType?: unknown;
  price?: CartTotalValueType | null;
  postalCode?: string;
  estimatedTime?: number;
  configuration: {
    json: string;
    description: string | null;
    erpProductId?: number;
    pimProductId?: number;
  };
  categories: Array<ICategoriesProduct>;

  discount?: {
    positionId: string;
    type: string;
    discountType: string;
    value: number;
    prices: {
      unit: {
        base: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
        current: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
        discounted: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
      };
      package: {
        base: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
        current: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
        discounted: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
      };
      total: {
        base: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
        current: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
        discounted: {
          net: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          gross: {
            taxRate: number;
            amount: number;
            currency: string;
          };
          assembly: {
            taxRate: number;
            amount: number;
            currency: string;
          };
        };
      };
    };
  };
  status?: string;
  rejectionReasonCode?: string;
};

interface ITaxRegime {
  code: string;
  description: string;
}

export type TOrderAddressType = {
  active: boolean;
  additionalData: IAdditionalData;
  street: string;
  streetNumber: string;
  apartmentNumber: string;
  city: string;
  country: string;
  district: string;
  createdAt: string;
  customer: string;
  customerType: string;
  default: boolean;
  email: string | null;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  id: number;
  taxId?: number;
  phoneNumber: IAddressV2PhoneNumber;
  postalCode: string;
  taxRegime?: ITaxRegime;
};

export type TOrderTransport = {
  transportGroupId: string;
  isBringService: boolean;
  bringPrice: { amount: number; currency: string };
  isAssemblyService: boolean;
  assemblyPrice: { amount: number; currency: string };
  totalPrice: { amount: number; currency: string };
  positions: Array<string>;
  vatRate: string | number;
  taxRate: string | number;
  estimatedLeadDate: string;
  customerExpectedDeliveryDate: {
    dateFrom: string;
    dateTo: string;
  };
  confirmedDeliveryDate: unknown;
  deliveryStatus: string;
  pricingWithRequiredServices: {
    gross: {
      amount: number;
      currency: string;
    };
  };
};

export type TDicountType = {
  discountType: string;
  amount: number;
};

export type TUserOrderType = {
  orderId: string;
  originOfferId: string;
  number: string;
  ownerId: string;
  customerId: string;
  customerAddressId: number | null;
  basicAddress?: TOrderAddressType;
  deliveryAddressId: number | null;
  deliveryAddress?: TOrderAddressType;
  invoiceAddressId: number | null;
  invoiceAddress?: TOrderAddressType;
  status: TOrderStatusType;
  createdDate: string;
  realizedDate?: string;
  expiryDate: string;
  groups: [
    {
      groupId: string;
      name: string;
      totalPrice: {
        amount: number;
        currency: string;
      };
      positions: [string];
    },
  ];
  transports: TOrderTransport[];
  positions: Array<PositionType>;
  totals: [OrderTotalType];
  discount: TDicountType;
  ownersStoreId: number;
  hasInvoice: boolean;
  canEdit: boolean;
  originType: string;
  orderPaymentStatus: TOrderPaymentStatusType;
  note: string;
  taxesTotals?: CartTaxesTotalGroup[];
  taxes?: CartTaxGroup[];
};

export type TUserPanelNavItemType = {
  name: string;
  url: string;
};

export type TUserPanelNavType = Array<TUserPanelNavItemType>;

export type TOrderPaymentOperationStatus = 'accepted' | 'not_accepted';

export enum TTransactionStatusType {
  initiated = 'initiated',
  initiation_cancelled = 'initiation_cancelled',
  processing = 'processing',
  cancellation_initiated = 'cancellation_initiated',
  canceled = 'canceled',
  failed = 'failed',
  rejected = 'rejected',
  refund_initiated = 'refund_initiated',
  refunded = 'refunded',
  accepted = 'accepted',
  status_unknown = 'status_unknown',
}

export type TOrderPaymentTransactionDetail = {
  operationStatus: TOrderPaymentOperationStatus;
  transactionId: string;
  transactionStatus: TTransactionStatusType;
  operationType: string;
  transactionType: string;
  amount: string;
  currency: string;
  cardData: {
    cardLastDigits: string;
    terminalName: string;
    option: string;
  } | null;
  deadline: string;
  acceptedDate: string;
  canCancel: boolean;
  canRefund: boolean;
  canEditCardData: boolean;
  canAccept: boolean;
  transactionUri: string;
  referenceOnlineTransactionId: string;
  onlineCheckoutProvider: string;
  onlineCheckoutMethod: string;
  additionalProviderData?: {
    id?: string;
    issueNumber?: string;
    sessionData?: string;
  };
};

export type TOrderPaymentDetail = {
  orderPaymentAmounts: {
    orderTotalAmount: string;
    orderLeftToPayAmount: string;
    currency: string;
    paidPercentage: string;
  };
  canRefundAnyValue: boolean;
  transactions: Array<TOrderPaymentTransactionDetail>;
};

export interface ICart {
  cartId: string;
  status: TCartStatusType;
  language?: string;
  positions: ICartItem[];
  totals: CartTotalsType[];
  taxesTotals?: CartTaxesTotalGroup[];
  taxes?: CartTaxGroup[];
}
